import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import { useAchievements } from "../contexts/AchievementContext";
import useViewportMode from "../hooks/useViewportMode";
import apiService from "../services/api";
import { requireAuth } from "../utils/authPrompt";
import "../styles/AIAssistant.css";
import "../styles/Notifications.css";

const WELCOME_MESSAGE =
  "Hi! I'm your Sous-chef. I can help with recipe ideas, ingredient swaps, meal planning, quick cooking tips, and questions about using PrepRight. Try asking things like: What can I make with chicken and tomatoes? How do shopping lists work? Where do I update my pantry?";

const QUICK_PROMPTS = [
  {
    title: "Pantry Dinner",
    prompt: "What should I cook from my pantry tonight?",
  },
  {
    title: "30-Min Meals",
    prompt: "Give me 30-minute dinner ideas",
  },
  {
    title: "Weekly Plan",
    prompt: "Plan a healthy week of meals",
  },
  {
    title: "Budget Prep",
    prompt: "Build a budget-friendly shopping plan",
  },
  {
    title: "Ingredient Swap",
    prompt: "What can I use instead of eggs in baking?",
  },
  {
    title: "Chicken Ideas",
    prompt: "What can I make with chicken and tomatoes?",
  },
];

const DESKTOP_INTENTS = [
  {
    title: "Recipe Builder",
    description: "Generate fresh meal ideas from your ingredients, mood, and time.",
    prompt: "I have chicken, tomatoes, garlic, and rice. What can I make tonight?",
  },
  {
    title: "Smart Substitutions",
    description: "Find reliable swaps when something is missing in your kitchen.",
    prompt: "I ran out of eggs while baking. What are the best substitutes?",
  },
  {
    title: "Weekly Structure",
    description: "Plan a realistic week with quick meals, leftovers, and variety.",
    prompt: "Can you build a healthy weekly meal plan with simple prep?",
  },
];

const AIAssistant = ({ sidebarOpen }) => {
  const { user } = useUser();
  const { isShowingAchievement, handleApiResponse } = useAchievements();
  const { isTouchLayout } = useViewportMode();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [retryAvailableAt, setRetryAvailableAt] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [, setPointsAwardedThisSession] = useState(false);
  const chatMessagesRef = useRef(null);
  const nextMessageIdRef = useRef(2);
  const sendingRef = useRef(false);

  const scrollChatToBottom = (behavior = "smooth") => {
    if (!chatMessagesRef.current) return;
    chatMessagesRef.current.scrollTo({
      top: chatMessagesRef.current.scrollHeight,
      behavior,
    });
  };
  useEffect(() => {
    document.body.classList.add("assistant-page");

    return () => {
      document.body.classList.remove("assistant-page");
    };
  }, []);

  const showSuccess = (message) => {
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000);
      return;
    }

    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 1,
          type: "assistant",
          content: WELCOME_MESSAGE,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      setPointsAwardedThisSession(false);
    }
  }, [messages.length]);

  useEffect(() => {
    setPointsAwardedThisSession(false);
  }, [user]);

  useEffect(() => {
    if (!retryAvailableAt) {
      setCooldownSeconds(0);
      return undefined;
    }

    const updateCooldown = () => {
      const remaining = Math.max(0, Math.ceil((retryAvailableAt - Date.now()) / 1000));
      setCooldownSeconds(remaining);
      if (remaining === 0) {
        setRetryAvailableAt(0);
      }
    };

    updateCooldown();
    const intervalId = window.setInterval(updateCooldown, 500);
    return () => window.clearInterval(intervalId);
  }, [retryAvailableAt]);

  const sendMessage = async (message) => {
    const normalizedMessage = String(message || "").trim();
    if (!normalizedMessage) return;
    if (isLoading || sendingRef.current) return;
    if (retryAvailableAt && Date.now() < retryAvailableAt) {
      const remaining = Math.max(1, Math.ceil((retryAvailableAt - Date.now()) / 1000));
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageIdRef.current++,
          type: "assistant",
          content: `Sous-chef needs about ${remaining} more seconds before trying again.`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      setTimeout(() => scrollChatToBottom("smooth"), 0);
      return;
    }

    if (!requireAuth(user, "Register or log in to chat with Sous-chef and get personalized cooking help.")) return;

    const userMessage = {
      id: nextMessageIdRef.current++,
      type: "user",
      content: normalizedMessage,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    sendingRef.current = true;
    setIsLoading(true);
    setTimeout(() => scrollChatToBottom("smooth"), 0);

    try {
      const response = await apiService.sendAIMessage(normalizedMessage, conversationHistory);

      const aiMessage = {
        id: nextMessageIdRef.current++,
        type: "assistant",
        content: response.response,
        timestamp: new Date().toLocaleTimeString(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setConversationHistory(response.conversationHistory);
      setTimeout(() => scrollChatToBottom("smooth"), 0);

      if (response && handleApiResponse) {
        handleApiResponse(response);
      }

      if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Great to chat with you! +${response.pointsEarned} points earned!`);
      } else if (response.pointsAwarded === false) {
        showSuccess("Great to chat with you! (You've already earned points for chatting today)");
      } else {
        showSuccess("Great to chat with you!");
      }
    } catch (error) {
      console.error("AI Chat error:", error);
      console.error("Error details:", error.response?.data || error.message);

      let errorContent = "Sorry, I'm having trouble right now. Please try again in a moment.";

      if (error.response?.status === 401) {
        errorContent = "Please log in to use Sous-chef.";
      } else if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfterSeconds;
        if (retryAfter) {
          setRetryAvailableAt(Date.now() + retryAfter * 1000);
        }
        errorContent = retryAfter
          ? `Google is rate-limiting Sous-chef right now. Please wait about ${retryAfter} seconds and try again.`
          : "Google is rate-limiting Sous-chef right now. Please wait a moment and try again.";
      } else if (error.response?.status === 500) {
        errorContent = "Sous-chef is temporarily unavailable. Please try again later.";
      } else if (error.code === "NETWORK_ERROR" || !navigator.onLine) {
        errorContent = "No internet connection. Please check your network and try again.";
      }

      const errorMessage = {
        id: nextMessageIdRef.current++,
        type: "assistant",
        content: errorContent,
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setTimeout(() => scrollChatToBottom("smooth"), 0);
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  const getCurrentTime = () => new Date().toLocaleTimeString();

  const formatAIResponse = (text) => {
    if (!text) return text;

    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/\n/g, "<br>")
      .replace(/(\d+\.\s+.*?)(?=\d+\.\s|$)/g, "<li>$1</li>")
      .replace(/(?:^|\n)(-\s+.*?)(?=\n-|\n\n|$)/g, "<li>$1</li>")
      .replace(/(<li>.*<\/li>)/g, "<ul>$1</ul>")
      .replace(/<\/ul><ul>/g, "")
      .replace(
        /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday):/g,
        '<div class="assistant-day-heading">$1:</div>'
      )
      .replace(/Cooking Tip:/g, '<div class="cooking-tip-header">Cooking Tip:</div>')
      .replace(/Weekend Idea:/g, '<div class="weekend-idea-header">Weekend Idea:</div>');
  };

  const renderMobileMessageContent = (message) => {
    const isAssistant = message.type === "assistant";
    const lines = (message.content || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const bulletLinePattern = /^[-*\u2022]\s*/;
    const looksLikeRecipeList =
      isAssistant &&
      lines.length >= 3 &&
      lines[0].toLowerCase().includes("idea") &&
      lines.slice(1).every((line) => bulletLinePattern.test(line));

    if (!looksLikeRecipeList) {
      return isAssistant ? (
        <div dangerouslySetInnerHTML={{ __html: formatAIResponse(message.content) }} />
      ) : (
        message.content
      );
    }

    const recipeItems = lines
      .slice(1)
      .map((line) => line.replace(bulletLinePattern, "").replace(/\*\*/g, "").trim())
      .filter(Boolean);

    return (
      <div className="mobile-assistant-recipe-block">
        <div className="mobile-assistant-recipe-intro">{lines[0]}</div>
        <div className="mobile-assistant-recipe-list">
          {recipeItems.map((item) => (
            <div key={item} className="mobile-assistant-recipe-item">
              <span className="mobile-assistant-recipe-text">{item}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPromptButtons = (prompts, className) => (
    <div className={className}>
      {prompts.map((item) => (
        <button
          key={item.prompt}
          type="button"
          className="assistant-prompt-btn"
          onClick={() => sendMessage(item.prompt)}
          disabled={isLoading || cooldownSeconds > 0}
        >
          <span>{item.title}</span>
        </button>
      ))}
    </div>
  );

  const renderChat = (mobile = false) => (
    <section className={`assistant-chat-shell ${mobile ? "assistant-chat-shell-mobile" : "assistant-chat-shell-desktop"}`}>
      <div className="chat-header">
        <div>
          <p className="chat-kicker">Live conversation</p>
          <h3 className="chat-title">Sous-chef</h3>
          <p className="chat-subtitle">Ask about recipes, substitutions, meal planning, prep flow, or using PrepRight.</p>
        </div>
        <p className="chat-time">{getCurrentTime()}</p>
      </div>

      <div className="chat-messages" ref={chatMessagesRef}>
        {messages.map((message) => (
          <div key={message.id} className={`chat-message ${message.type}`}>
            <div className="message-content">
              {mobile ? (
                renderMobileMessageContent(message)
              ) : message.type === "assistant" ? (
                <div dangerouslySetInnerHTML={{ __html: formatAIResponse(message.content) }} />
              ) : (
                message.content
              )}
            </div>
            <div className="message-time">{message.timestamp}</div>
          </div>
        ))}

        {isLoading && (
          <div className="loading-indicator-compact">
            <div className="typing-indicator-small">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="loading-text">Sous-chef is thinking...</span>
          </div>
        )}

      </div>

      <form className="chat-input-container" onSubmit={handleSubmit}>
        <input
          type="text"
          className="chat-input"
          placeholder="Ask about meals, pantry recipes, substitutions, prep plans, or the app..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          disabled={isLoading || cooldownSeconds > 0}
        />
        <button type="submit" className="send-btn" disabled={isLoading || cooldownSeconds > 0 || !inputMessage.trim()}>
          {cooldownSeconds > 0 ? `${cooldownSeconds}s` : "Send"}
        </button>
      </form>
    </section>
  );

  const renderDesktop = () => (
    <div className="assistant-workspace">
      <aside className="assistant-rail">
        <div className="assistant-hero-card">
          <p className="assistant-hero-kicker">Kitchen Co-Pilot</p>
          <h1 className="assistant-hero-title">Sous-chef for faster decisions, better meals, less stress.</h1>
          <p className="assistant-hero-copy">
            Use one tap prompts, detailed meal planning help, and contextual cooking support tailored to your pantry.
          </p>
        </div>

        <div className="assistant-intent-grid">
          {DESKTOP_INTENTS.map((intent) => (
            <button
              key={intent.title}
              type="button"
              className="assistant-intent-card"
              onClick={() => sendMessage(intent.prompt)}
              disabled={isLoading || cooldownSeconds > 0}
            >
              <h3>{intent.title}</h3>
              <p>{intent.description}</p>
            </button>
          ))}
        </div>

        <div className="assistant-prompt-panel">
          <p className="assistant-panel-title">Quick start prompts</p>
          {renderPromptButtons(QUICK_PROMPTS, "assistant-prompt-grid")}
        </div>
      </aside>

      {renderChat(false)}
    </div>
  );

  const renderMobile = () => (
    <div className="assistant-mobile-layout">
      <header className="assistant-mobile-header">
        <p className="assistant-hero-kicker">Kitchen Co-Pilot</p>
        <h1 className="assistant-mobile-title">Sous-chef</h1>
        <p className="assistant-mobile-subtitle">Responsive help for recipes, swaps, meal plans, and app questions.</p>
      </header>

      {renderPromptButtons(QUICK_PROMPTS.slice(0, 5), "assistant-mobile-prompt-row")}

      {renderChat(true)}
    </div>
  );

  return (
    <div className={`ai-container ${sidebarOpen ? "sidebar-open" : ""}`}>
      {isTouchLayout ? renderMobile() : renderDesktop()}

      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">{'\u2705'}</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
